import React, { useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { useScroll } from 'react-use-gesture'
import { html2Images, CARD_WIDTH } from 'global'
import { IHand } from 'models'
import { ResultCard, Box, IBoxProps, Card } from 'components'

const CardsContainer = styled(Box)`
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
`

interface IResultCards extends IBoxProps {
  hand: IHand
  selected: number
  onSelected: (index: number) => void
  onLoaded?: (cardImgs: string[]) => void
  children?: (props: IBoxProps) => React.ReactNode
}

// Adds "padding" to either sides of the scroll container to center first and last cast
// at maximum scroll positions. We are using the Card here because various css techniques have
// either been unsuccessful with adding padding/margin to either sides of the scroll items, OR,
// caused issues with html2canvas and the image download feature.
const SpacerCard = () => (
  <Card
    transparent
    outerProps={{
      // 50% of width - half of card size.
      minWidth: 'calc(50% - 135px);',
    }}
  />
)

export const ResultCards = ({
  hand,
  onLoaded,
  selected,
  onSelected,
  children,
  ...props
}: IResultCards) => {
  const ref = React.useRef<HTMLDivElement>(null)
  const { cards, color } = hand

  let readyCards = 0
  const handleCardReady = () => {
    readyCards += 1
    if (readyCards === cards.length) {
      setTimeout(saveCardImages)
    }
  }

  const saveCardImages = useCallback(async () => {
    if (ref.current) {
      if (onLoaded) {
        const cardImgs = await html2Images(
          ref.current.querySelectorAll('.result-card')
        )
        onLoaded(cardImgs)
      }
    } else {
      console.error(
        'Unable to parse cards to images, PDF download is disabled.'
      )
    }
  }, [ref, onLoaded])

  const scroll = useScroll(
    ({ scrolling, xy: [x] }) => {
      if (!scrolling) {
        // Dividing the x scroll position with the cardWidth gives how many cards
        // the scroll has travelled, then by rounding it, we can switch to the
        // next card when the card is in the half way point.
        onSelected(Math.round(x / CARD_WIDTH))
      }
    },
    {
      domTarget: ref,
    }
  )

  useEffect(scroll, [scroll])

  useEffect(() => {
    // The cards need to be within viewport before html2images is called =\
    window.scrollTo(0, 0)

    // Select the first card be default
    onSelected(0)

    // Still emit `onLoaded()` if there are no cards
    if (!cards.length) {
      setTimeout(saveCardImages)
    }
  }, [cards, saveCardImages, onSelected])

  return (
    <CardsContainer ref={ref}>
      <SpacerCard />
      {children && children(props)}
      {cards.map((card, i) => (
        <ResultCard
          key={
            card.category
              ? `${card.question.id}${card.category}`
              : `${card.question.id}`
          }
          card={card}
          handColor={color}
          onLoaded={handleCardReady}
          {...props}
          // Allows perfect centering of first and last cards over the download card button
          marginRight={i === cards.length - 1 ? '0' : undefined}
        />
      ))}
      <SpacerCard />
    </CardsContainer>
  )
}
